<template>
  <div class="creators-page">

    <!-- Header -->
    <div class="page-header">
      <div>
        <h1>Creators</h1>
        <p>Manage creators, mentors and their platform profiles.</p>
      </div>

      <button class="primary-btn" @click="openAddModal">
        <i class="bx bx-plus"></i>
        Add Creator
      </button>
    </div>

    <!-- Filters -->
    <div class="filter-card">

      <div class="search-box">
        <i class="bx bx-search"></i>

        <input
          v-model="search"
          type="text"
          placeholder="Search creators..."
        />
      </div>

      <select v-model="selectedDesignation">
        <option value="">All Designations</option>

        <option
          v-for="designation in designations"
          :key="designation"
          :value="designation"
        >
          {{ designation }}
        </option>
      </select>

      <select v-model="selectedBusiness">
        <option value="">All Businesses</option>

        <option
          v-for="business in businesses"
          :key="business"
          :value="business"
        >
          {{ business }}
        </option>
      </select>

      <button
        class="reset-btn"
        @click="resetFilters"
      >
        <i class="bx bx-reset"></i>
        Reset
      </button>

    </div>

    <!-- Table -->
    <div class="table-card">

      <div class="table-top">
        <div>
          <h3>Creator List</h3>
          <span>
            {{ filteredCreators.length }} creators
          </span>
        </div>
      </div>

      <div class="table-wrapper">

        <table>

          <thead>
            <tr>
              <th>Creator</th>
              <th>Business</th>
              <th>Designation</th>
              <th>Experience</th>
              <th>Following</th>
              <th>Reach</th>
              <th>Channels</th>
              <th>Created</th>
              <th class="action-col">Actions</th>
            </tr>
          </thead>

          <tbody>

            <tr
              v-for="creator in creators"
              :key="creator.creator_id"
            >

              <!-- Creator -->
              <td>
                <div class="creator-info">

                  <div class="creator-avatar">

                    <img
                      v-if="creator.cre_profile_pic"
                      :src="creator.cre_profile_pic"
                      :alt="creator.cre_username"
                    />

                    <span v-else>
                      {{ getInitials(creator.cre_username) }}
                    </span>

                  </div>

                  <div>
                    <strong>
                      {{ creator.cre_username }}
                    </strong>

                    <small>
                      #{{ creator.creator_id }}
                    </small>
                  </div>

                </div>
              </td>

              <!-- Business -->
              <td>
                <span class="business-name">
                  {{ creator.cre_buss_name || '-' }}
                </span>
              </td>

              <!-- Designation -->
              <td>
                <span class="designation-badge">
                  {{ creator.cre_designation || '-' }}
                </span>
              </td>

              <!-- Experience -->
              <td>
                {{ creator.cre_exprience || '-' }}
              </td>

              <!-- Following -->
              <td>
                {{ creator.cre_following || '-' }}
              </td>

              <!-- Reach -->
              <td>
                {{ creator.cre_reach || '-' }}
              </td>

              <!-- Channels -->
              <td>
                <span
                  v-if="creator.cre_channels"
                  class="channel-badge"
                >
                  <i class="bx bx-broadcast"></i>
                  Available
                </span>

                <span
                  v-else
                  class="muted"
                >
                  -
                </span>
              </td>

              <!-- Created -->
              <td>
                {{ formatDate(creator.cre_datetime) }}
              </td>

              <!-- Actions -->
              <td>

                <div class="actions">

                  <button
                    class="icon-btn view"
                    title="View"
                    @click="viewCreator(creator)"
                  >
                    <i class="bx bx-show"></i>
                  </button>

                  <button
                    class="icon-btn edit"
                    title="Edit"
                    @click="editCreator(creator)"
                  >
                    <i class="bx bx-edit"></i>
                  </button>

                  <button
                    class="icon-btn delete"
                    title="Delete"
                    @click="confirmDelete(creator)"
                  >
                    <i class="bx bx-trash"></i>
                  </button>

                </div>

              </td>

            </tr>

            <!-- Empty -->
            <tr v-if="creators.length === 0">

              <td colspan="9">

                <div class="empty-state">

                  <i class="bx bx-user-circle"></i>

                  <h4>No creators found</h4>

                  <p>
                    Try changing your search or filters.
                  </p>

                </div>

              </td>

            </tr>

          </tbody>

        </table>

      </div>

      <!-- Pagination -->
      <div
        v-if="filteredCreators.length > 0"
        class="pagination"
      >

        <span>
          Showing
          {{ startIndex + 1 }}
          -
          {{ Math.min(endIndex, filteredCreators.length) }}
          of
          {{ filteredCreators.length }}
        </span>

        <div class="pagination-buttons">

          <button
            :disabled="currentPage === 1"
            @click="currentPage--"
          >
            <i class="bx bx-chevron-left"></i>
          </button>

          <button
            v-for="page in totalPages"
            :key="page"
            :class="{ active: currentPage === page }"
            @click="currentPage = page"
          >
            {{ page }}
          </button>

          <button
            :disabled="currentPage === totalPages"
            @click="currentPage++"
          >
            <i class="bx bx-chevron-right"></i>
          </button>

        </div>

      </div>

    </div>

    <!-- Add / Edit Modal -->
    <div
      v-if="showFormModal"
      class="modal-overlay"
      @click.self="closeFormModal"
    >

      <div class="modal">

        <div class="modal-header">

          <div>
            <h3>
              {{ isEditing ? 'Edit Creator' : 'Add Creator' }}
            </h3>

            <p>
              {{
                isEditing
                  ? 'Update creator information.'
                  : 'Create a new creator profile.'
              }}
            </p>
          </div>

          <button
            class="close-btn"
            @click="closeFormModal"
          >
            <i class="bx bx-x"></i>
          </button>

        </div>

        <div class="modal-body">

          <div class="form-grid">

           
            <!-- Username -->
            <div class="form-group">

              <label>Username <span>*</span></label>

              <input
                v-model="form.cre_username"
                type="text"
                placeholder="Enter creator username"
              />

            </div>

            <!-- Business -->
            <div class="form-group">

              <label>Business Name</label>

              <input
                v-model="form.cre_buss_name"
                type="text"
                placeholder="Enter business name"
              />

            </div>

            <!-- Designation -->
            <div class="form-group">

              <label>Designation</label>

              <input
                v-model="form.cre_designation"
                type="text"
                placeholder="e.g. Founder, Mentor"
              />

            </div>

            <!-- Experience -->
            <div class="form-group">

              <label>Experience</label>

              <input
                v-model="form.cre_exprience"
                type="text"
                placeholder="e.g. 10 Years"
              />

            </div>

            <!-- Following -->
            <div class="form-group">

              <label>Following</label>

              <input
                v-model="form.cre_following"
                type="text"
                placeholder="e.g. 125K"
              />

            </div>

            <!-- Reach -->
            <div class="form-group">

              <label>Reach</label>

              <input
                v-model="form.cre_reach"
                type="text"
                placeholder="e.g. 2.5M"
              />

            </div>

          
            <!-- Profile Pic -->
            <div class="form-group full">

              <label>Profile Picture URL</label>

              <input
                v-model="form.cre_profile_pic"
                type="text"
                placeholder="Enter profile image URL"
              />

            </div>

            <!-- Channels -->
            <div class="form-group full">

              <label>Channels</label>

              <textarea
                v-model="form.cre_channels"
                rows="4"
                placeholder="Enter creator channels"
              ></textarea>

            </div>

            <!-- Tags -->
            <div class="form-group full">

              <label>Tags</label>

              <textarea
                v-model="form.cre_tags"
                rows="3"
                placeholder="creator, mentor, technology"
              ></textarea>

            </div>

            <!-- Short Comment -->
            <div class="form-group full">

              <label>Short Comment</label>

              <textarea
                v-model="form.cre_short_comment"
                rows="4"
                placeholder="Enter short creator description"
              ></textarea>

            </div>

          </div>

        </div>

        <div class="modal-footer">

          <button
            class="secondary-btn"
            @click="closeFormModal"
          >
            Cancel
          </button>

          <button
            class="primary-btn"
            @click="saveCreator"
          >
            <i class="bx bx-save"></i>

            {{
              isEditing
                ? 'Update Creator'
                : 'Create Creator'
            }}
          </button>

        </div>

      </div>

    </div>

    <!-- View Modal -->
    <div
      v-if="showViewModal"
      class="modal-overlay"
      @click.self="showViewModal = false"
    >

      <div class="modal view-modal">

        <div class="modal-header">

          <div>
            <h3>Creator Details</h3>
            <p>View creator profile information.</p>
          </div>

          <button
            class="close-btn"
            @click="showViewModal = false"
          >
            <i class="bx bx-x"></i>
          </button>

        </div>

        <div
          v-if="selectedCreator"
          class="modal-body"
        >

          <!-- Profile -->
          <div class="creator-preview">

            <div class="preview-avatar">

              <img
                v-if="selectedCreator.cre_profile_pic"
                :src="selectedCreator.cre_profile_pic"
                :alt="selectedCreator.cre_username"
              />

              <span v-else>
                {{ getInitials(selectedCreator.cre_username) }}
              </span>

            </div>

            <div>

              <h2>
                {{ selectedCreator.cre_username }}
              </h2>

              <p>
                {{
                  selectedCreator.cre_designation ||
                  'Creator'
                }}

                <span>•</span>

                {{
                  selectedCreator.cre_buss_name ||
                  'Independent Creator'
                }}
              </p>

            </div>

          </div>

          <!-- Details -->
          <div class="detail-grid">

            <div class="detail-item">

              <label>Creator ID</label>

              <strong>
                #{{ selectedCreator.creator_id }}
              </strong>

            </div>


            <div class="detail-item">

              <label>Business</label>

              <strong>
                {{
                  selectedCreator.cre_buss_name || '-'
                }}
              </strong>

            </div>

            <div class="detail-item">

              <label>Designation</label>

              <strong>
                {{
                  selectedCreator.cre_designation || '-'
                }}
              </strong>

            </div>

            <div class="detail-item">

              <label>Experience</label>

              <strong>
                {{
                  selectedCreator.cre_exprience || '-'
                }}
              </strong>

            </div>

            <div class="detail-item">

              <label>Following</label>

              <strong>
                {{
                  selectedCreator.cre_following || '-'
                }}
              </strong>

            </div>

            <div class="detail-item">

              <label>Reach</label>

              <strong>
                {{
                  selectedCreator.cre_reach || '-'
                }}
              </strong>

            </div>

            <div class="detail-item">

              <label>Created</label>

              <strong>
                {{
                  formatDate(
                    selectedCreator.cre_datetime
                  )
                }}
              </strong>

            </div>

          </div>

          <!-- Short Comment -->
          <div class="content-section">

            <label>Short Comment</label>

            <div class="content-box">

              {{
                selectedCreator.cre_short_comment ||
                'No short comment available.'
              }}

            </div>

          </div>

          <!-- Channels -->
          <div class="content-section">

            <label>Channels</label>

            <div class="content-box">

              {{
                selectedCreator.cre_channels ||
                'No channels available.'
              }}

            </div>

          </div>

          <!-- Tags -->
          <div class="content-section">

            <label>Tags</label>

            <div class="content-box">

              {{
                selectedCreator.cre_tags ||
                'No tags available.'
              }}

            </div>

          </div>

        </div>

        <div class="modal-footer">

          <button
            class="secondary-btn"
            @click="showViewModal = false"
          >
            Close
          </button>

          <button
            class="primary-btn"
            @click="editCreator(selectedCreator)"
          >
            <i class="bx bx-edit"></i>
            Edit Creator
          </button>

        </div>

      </div>

    </div>

    <!-- Delete Modal -->
    <div
      v-if="showDeleteModal"
      class="modal-overlay"
      @click.self="showDeleteModal = false"
    >

      <div class="delete-modal">

        <div class="delete-icon">
          <i class="bx bx-trash"></i>
        </div>

        <h3>Delete Creator?</h3>

        <p>

          Are you sure you want to delete

          <strong>
            {{ selectedCreator?.cre_username }}
          </strong>?

          This action cannot be undone.

        </p>

        <div class="delete-actions">

          <button
            class="secondary-btn"
            @click="showDeleteModal = false"
          >
            Cancel
          </button>

          <button
            class="danger-btn"
            @click="deleteCreator"
          >
            Delete Creator
          </button>

        </div>

      </div>

    </div>

  </div>
</template>
<script>
import {
  getCreatorsApi,
  getCreatorApi,
  createCreatorApi,
  updateCreatorApi,
  deleteCreatorApi,
  getCreatorStatsApi,
} from "@/apis/creatorapis";

export default {
  name: "CreatorsScreen",

  data() {
    return {
      // ===============================
      // FILTERS
      // ===============================
      search: "",
      selectedDesignation: "",
      selectedBusiness: "",

      // ===============================
      // PAGINATION
      // ===============================
      currentPage: 1,
      perPage: 10,
      totalCreators: 0,
      totalPagesCount: 1,

      // ===============================
      // DATA
      // ===============================
      creators: [],

      // ===============================
      // FILTER OPTIONS
      // ===============================
      designations: [],
      businesses: [],

      // ===============================
      // STATS
      // ===============================
      stats: {
        totalCreators: 0,
        newCreatorsThisMonth: 0,
      },

      // ===============================
      // LOADING
      // ===============================
      loading: false,
      saving: false,
      deleting: false,

      // ===============================
      // MODALS
      // ===============================
      showFormModal: false,
      showViewModal: false,
      showDeleteModal: false,

      isEditing: false,
      selectedCreator: null,

      // ===============================
      // FORM
      // ===============================
      form: {
        cre_username: "",
        cre_buss_name: "",
        cre_designation: "",
        cre_profile_pic: "",
        cre_exprience: "",
        cre_short_comment: "",
        cre_following: "",
        cre_reach: "",
        cre_channels: "",
        cre_tags: "",
      },
    };
  },

  computed: {
    filteredCreators() {
      return this.creators;
    },

    startIndex() {
      if (!this.totalCreators) return 0;

      return (
        (this.currentPage - 1) *
        this.perPage
      );
    },

    endIndex() {
      return (
        this.startIndex +
        this.creators.length
      );
    },
  },

  watch: {
    search() {
      this.currentPage = 1;
      this.loadCreators();
    },

    selectedDesignation() {
      this.currentPage = 1;
      this.loadCreators();
    },

    selectedBusiness() {
      this.currentPage = 1;
      this.loadCreators();
    },
  },

  mounted() {
    this.loadCreators();
    this.loadStats();
  },

  methods: {
    // =====================================================
    // LOAD CREATORS
    // =====================================================

    async loadCreators() {
      this.loading = true;

      try {
        const res = await getCreatorsApi({
          page: this.currentPage,
          limit: this.perPage,
          search: this.search,
          designation:
            this.selectedDesignation,
          business:
            this.selectedBusiness,
        });

        if (res && res.status !== false) {
          this.creators = res.creators || [];

          this.totalCreators = Number(
            res.total || 0
          );

          this.totalPagesCount = Math.max(
            Number(res.totalPages || 1),
            1
          );

          this.currentPage = Number(
            res.page || this.currentPage
          );

          /*
           * Backend can optionally return filter
           * options. If available, use them.
           */
          if (Array.isArray(res.designations)) {
            this.designations =
              res.designations;
          }

          if (Array.isArray(res.businesses)) {
            this.businesses =
              res.businesses;
          }

          /*
           * If backend does not return options,
           * generate them from currently loaded data.
           */
          this.buildFilterOptions();
        } else {
          this.creators = [];
          this.totalCreators = 0;
          this.totalPagesCount = 1;
        }
      } catch (error) {
        console.error(
          "Load Creators Error:",
          error
        );

        this.creators = [];
        this.totalCreators = 0;
        this.totalPagesCount = 1;
      } finally {
        this.loading = false;
      }
    },

    // =====================================================
    // LOAD STATS
    // =====================================================

    async loadStats() {
      try {
        const res = await getCreatorStatsApi();

        if (
          res &&
          res.status !== false &&
          res.stats
        ) {
          this.stats = {
            ...this.stats,
            ...res.stats,
          };
        }
      } catch (error) {
        console.error(
          "Load Creator Stats Error:",
          error
        );
      }
    },

    // =====================================================
    // FILTER OPTIONS
    // =====================================================

    buildFilterOptions() {
      /*
       * Only replace options if backend did not provide
       * them.
       */
      if (!this.designations.length) {
        this.designations = [
          ...new Set(
            this.creators
              .map(
                (creator) =>
                  creator.cre_designation
              )
              .filter(Boolean)
          ),
        ];
      }

      if (!this.businesses.length) {
        this.businesses = [
          ...new Set(
            this.creators
              .map(
                (creator) =>
                  creator.cre_buss_name
              )
              .filter(Boolean)
          ),
        ];
      }
    },

    // =====================================================
    // INITIALS
    // =====================================================

    getInitials(name) {
      if (!name) return "C";

      return name
        .split(" ")
        .filter(Boolean)
        .map((word) =>
          word.charAt(0)
        )
        .slice(0, 2)
        .join("")
        .toUpperCase();
    },

    // =====================================================
    // DATE
    // =====================================================

    formatDate(date) {
      if (!date) return "-";

      const value = new Date(date);

      if (
        Number.isNaN(value.getTime())
      ) {
        return date;
      }

      return value.toLocaleDateString(
        "en-IN",
        {
          day: "2-digit",
          month: "short",
          year: "numeric",
        }
      );
    },

    // =====================================================
    // RESET FILTERS
    // =====================================================

    resetFilters() {
      this.search = "";
      this.selectedDesignation = "";
      this.selectedBusiness = "";
      this.currentPage = 1;

      this.loadCreators();
    },

    // =====================================================
    // RESET FORM
    // =====================================================

    resetForm() {
      this.form = {
        cre_username: "",
        cre_buss_name: "",
        cre_designation: "",
        cre_profile_pic: "",
        cre_exprience: "",
        cre_short_comment: "",
        cre_following: "",
        cre_reach: "",
        cre_channels: "",
        cre_tags: "",
        created_by: "",
      };
    },

    // =====================================================
    // ADD CREATOR
    // =====================================================

    openAddModal() {
      this.isEditing = false;
      this.selectedCreator = null;

      this.resetForm();

      this.showFormModal = true;
    },

    // =====================================================
    // CLOSE FORM
    // =====================================================

    closeFormModal() {
      if (this.saving) return;

      this.showFormModal = false;

      this.resetForm();

      this.selectedCreator = null;
      this.isEditing = false;
    },

    // =====================================================
    // VIEW CREATOR
    // =====================================================

    async viewCreator(creator) {
      if (!creator?.creator_id) {
        return;
      }

      try {
        const res = await getCreatorApi(
          creator.creator_id
        );

        if (
          res &&
          res.status !== false &&
          res.creator
        ) {
          this.selectedCreator =
            res.creator;
        } else {
          this.selectedCreator =
            creator;
        }

        this.showViewModal = true;
      } catch (error) {
        console.error(
          "View Creator Error:",
          error
        );

        this.selectedCreator = creator;
        this.showViewModal = true;
      }
    },

    // =====================================================
    // CLOSE VIEW
    // =====================================================

    closeViewModal() {
      this.showViewModal = false;
      this.selectedCreator = null;
    },

    // =====================================================
    // EDIT CREATOR
    // =====================================================

    async editCreator(creator) {
      if (!creator?.creator_id) {
        return;
      }

      try {
        const res = await getCreatorApi(
          creator.creator_id
        );

        const data =
          res &&
          res.status !== false &&
          res.creator
            ? res.creator
            : creator;

        this.selectedCreator = data;

        this.isEditing = true;

        this.form = {
          creator_id:
            data.creator_id,


          cre_username:
            data.cre_username || "",

          cre_buss_name:
            data.cre_buss_name || "",

          cre_designation:
            data.cre_designation || "",

          cre_profile_pic:
            data.cre_profile_pic || "",

          cre_exprience:
            data.cre_exprience || "",

          cre_short_comment:
            data.cre_short_comment || "",

          cre_following:
            data.cre_following || "",

          cre_reach:
            data.cre_reach || "",

          cre_channels:
            data.cre_channels || "",

          cre_tags:
            data.cre_tags || "",

          created_by:
            data.created_by || "",
        };

        this.showViewModal = false;
        this.showFormModal = true;
      } catch (error) {
        console.error(
          "Edit Creator Error:",
          error
        );
      }
    },

    // =====================================================
    // SAVE CREATOR
    // =====================================================

    async saveCreator() {
      if (
        !this.form.cre_username ||
        !this.form.cre_username.trim()
      ) {
        alert(
          "Please enter creator username."
        );
        return;
      }

      this.saving = true;

      try {
        let res;

        if (this.isEditing) {
          if (
            !this.selectedCreator?.creator_id
          ) {
            alert(
              "Creator ID is missing."
            );
            return;
          }

          res = await updateCreatorApi({
            creator_id:
              this.selectedCreator
                .creator_id,

            ...this.form,
          });
        } else {
          res = await createCreatorApi(
            this.form
          );
        }

        if (
          res &&
          res.status !== false
        ) {
          alert(
            res.success ||
              (this.isEditing
                ? "Creator updated successfully."
                : "Creator created successfully.")
          );

          this.showFormModal = false;

          this.resetForm();

          this.selectedCreator = null;

          this.isEditing = false;

          this.currentPage = 1;

          await this.loadCreators();
          await this.loadStats();
        } else {
          alert(
            res?.success ||
              "Creator save failed."
          );
        }
      } catch (error) {
        console.error(
          "Save Creator Error:",
          error
        );

        alert(
          error?.response?.data?.success ||
            "Something went wrong while saving creator."
        );
      } finally {
        this.saving = false;
      }
    },

    // =====================================================
    // DELETE CONFIRM
    // =====================================================

    confirmDelete(creator) {
      this.selectedCreator = creator;
      this.showDeleteModal = true;
    },

    // =====================================================
    // CLOSE DELETE
    // =====================================================

    closeDeleteModal() {
      if (this.deleting) return;

      this.showDeleteModal = false;
      this.selectedCreator = null;
    },

    // =====================================================
    // DELETE CREATOR
    // =====================================================

    async deleteCreator() {
      if (
        !this.selectedCreator?.creator_id
      ) {
        return;
      }

      this.deleting = true;

      try {
        const res =
          await deleteCreatorApi(
            this.selectedCreator
              .creator_id
          );

        if (
          res &&
          res.status !== false
        ) {
          alert(
            res.success ||
              "Creator deleted successfully."
          );

          this.showDeleteModal = false;
          this.selectedCreator = null;

          /*
           * If deleting the last item of the
           * current page, move back one page.
           */
          if (
            this.creators.length === 1 &&
            this.currentPage > 1
          ) {
            this.currentPage--;
          }

          await this.loadCreators();
          await this.loadStats();
        } else {
          alert(
            res?.success ||
              "Creator deletion failed."
          );
        }
      } catch (error) {
        console.error(
          "Delete Creator Error:",
          error
        );

        alert(
          error?.response?.data?.success ||
            "Something went wrong while deleting creator."
        );
      } finally {
        this.deleting = false;
      }
    },

    // =====================================================
    // PAGINATION
    // =====================================================

    goToPage(page) {
      if (
        page < 1 ||
        page > this.totalPagesCount ||
        page === this.currentPage
      ) {
        return;
      }

      this.currentPage = page;
      this.loadCreators();
    },

    previousPage() {
      if (this.currentPage <= 1) {
        return;
      }

      this.currentPage--;

      this.loadCreators();
    },

    nextPage() {
      if (
        this.currentPage >=
        this.totalPagesCount
      ) {
        return;
      }

      this.currentPage++;

      this.loadCreators();
    },
  },
};
</script>

<style scoped>
.creators-page {
  min-height: 100%;
  background: #f8fafc;
  padding: 24px;
  color: #0f172a;
}

/* Header */
.page-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 20px;
  margin-bottom: 22px;
}

.page-header h1 {
  margin: 0 0 5px;
  font-size: 24px;
  font-weight: 700;
}

.page-header p {
  margin: 0;
  color: #64748b;
  font-size: 14px;
}

/* Buttons */
.primary-btn,
.secondary-btn,
.danger-btn,
.reset-btn {
  border: 0;
  border-radius: 9px;
  padding: 10px 16px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 7px;
  transition: 0.2s;
}

.primary-btn {
  background: #2563eb;
  color: #fff;
}

.primary-btn:hover {
  background: #1d4ed8;
}

.secondary-btn {
  background: #f1f5f9;
  color: #334155;
}

.secondary-btn:hover {
  background: #e2e8f0;
}

.danger-btn {
  background: #dc2626;
  color: #fff;
}

.danger-btn:hover {
  background: #b91c1c;
}

/* Filters */
.filter-card {
  background: #fff;
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  padding: 15px;
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 18px;
}

.search-box {
  position: relative;
  flex: 1;
  min-width: 250px;
}

.search-box i {
  position: absolute;
  left: 13px;
  top: 50%;
  transform: translateY(-50%);
  color: #94a3b8;
  font-size: 18px;
}

.search-box input,
.filter-card select {
  height: 42px;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  background: #fff;
  color: #334155;
  font-size: 14px;
  outline: none;
}

.search-box input {
  width: 100%;
  padding: 0 12px 0 40px;
}

.filter-card select {
  min-width: 190px;
  padding: 0 35px 0 12px;
}

.search-box input:focus,
.filter-card select:focus,
.form-group input:focus,
.form-group textarea:focus {
  border-color: #2563eb;
}

.reset-btn {
  height: 42px;
  background: #f1f5f9;
  color: #475569;
}

/* Table */
.table-card {
  background: #fff;
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  overflow: hidden;
}

.table-top {
  padding: 18px 20px;
  border-bottom: 1px solid #e2e8f0;
}

.table-top h3 {
  margin: 0 0 4px;
  font-size: 16px;
}

.table-top span {
  color: #64748b;
  font-size: 13px;
}

.table-wrapper {
  width: 100%;
  overflow-x: auto;
}

table {
  width: 100%;
  min-width: 1250px;
  border-collapse: collapse;
}

th {
  background: #f8fafc;
  color: #64748b;
  font-size: 12px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.03em;
  padding: 13px 16px;
  text-align: left;
  white-space: nowrap;
}

td {
  padding: 15px 16px;
  border-top: 1px solid #f1f5f9;
  font-size: 13px;
  color: #475569;
  white-space: nowrap;
}

tr:hover td {
  background: #fafcff;
}

.action-col {
  text-align: right;
}

.creator-info {
  display: flex;
  align-items: center;
  gap: 11px;
  min-width: 210px;
}

.creator-avatar {
  width: 42px;
  height: 42px;
  border-radius: 50%;
  overflow: hidden;
  background: #eff6ff;
  color: #2563eb;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 13px;
  font-weight: 700;
  flex-shrink: 0;
}

.creator-avatar img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.creator-info strong {
  display: block;
  color: #0f172a;
  font-size: 13px;
  font-weight: 600;
}

.creator-info small {
  display: block;
  margin-top: 3px;
  color: #94a3b8;
  font-size: 11px;
}

.business-name {
  color: #334155;
  font-weight: 500;
}

.designation-badge {
  display: inline-flex;
  padding: 5px 9px;
  border-radius: 6px;
  background: #f1f5f9;
  color: #475569;
  font-size: 12px;
}

.channel-badge {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  color: #2563eb;
  font-size: 12px;
  font-weight: 600;
}

.muted {
  color: #94a3b8;
}

.actions {
  display: flex;
  justify-content: flex-end;
  gap: 6px;
}

.icon-btn {
  width: 34px;
  height: 34px;
  border-radius: 7px;
  border: 1px solid #e2e8f0;
  background: #fff;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 17px;
}

.icon-btn.view {
  color: #475569;
}

.icon-btn.edit {
  color: #2563eb;
}

.icon-btn.delete {
  color: #dc2626;
}

.icon-btn:hover {
  background: #f8fafc;
}

/* Empty */
.empty-state {
  padding: 55px 20px;
  text-align: center;
}

.empty-state i {
  font-size: 42px;
  color: #cbd5e1;
}

.empty-state h4 {
  margin: 10px 0 5px;
  color: #334155;
}

.empty-state p {
  margin: 0;
  color: #94a3b8;
}

/* Pagination */
.pagination {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 15px 20px;
  border-top: 1px solid #e2e8f0;
  color: #64748b;
  font-size: 13px;
}

.pagination-buttons {
  display: flex;
  gap: 5px;
}

.pagination-buttons button {
  min-width: 34px;
  height: 34px;
  border: 1px solid #e2e8f0;
  background: #fff;
  color: #475569;
  border-radius: 7px;
  cursor: pointer;
}

.pagination-buttons button.active {
  background: #2563eb;
  color: #fff;
  border-color: #2563eb;
}

.pagination-buttons button:disabled {
  opacity: 0.45;
  cursor: not-allowed;
}

/* Modal */
.modal-overlay {
  position: fixed;
  inset: 0;
  background: rgba(15, 23, 42, 0.5);
  z-index: 1000;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 20px;
}

.modal {
  width: 780px;
  max-width: 100%;
  max-height: 90vh;
  overflow-y: auto;
  background: #fff;
  border-radius: 14px;
  box-shadow: 0 20px 50px rgba(15, 23, 42, 0.2);
}

.modal-header {
  padding: 20px 22px;
  border-bottom: 1px solid #e2e8f0;
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
}

.modal-header h3 {
  margin: 0 0 4px;
  font-size: 18px;
}

.modal-header p {
  margin: 0;
  color: #64748b;
  font-size: 13px;
}

.close-btn {
  width: 34px;
  height: 34px;
  border: 0;
  border-radius: 7px;
  background: #f1f5f9;
  color: #475569;
  font-size: 20px;
  cursor: pointer;
}

.modal-body {
  padding: 22px;
}

.form-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 17px;
}

.form-group {
  display: flex;
  flex-direction: column;
  gap: 7px;
}

.form-group.full {
  grid-column: 1 / -1;
}

.form-group label {
  color: #334155;
  font-size: 13px;
  font-weight: 600;
}

.form-group label span {
  color: #dc2626;
}

.form-group input,
.form-group textarea {
  width: 100%;
  box-sizing: border-box;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  padding: 11px 12px;
  color: #334155;
  font-size: 14px;
  outline: none;
  background: #fff;
}

.form-group textarea {
  resize: vertical;
  min-height: 90px;
}

.modal-footer {
  padding: 16px 22px;
  border-top: 1px solid #e2e8f0;
  display: flex;
  justify-content: flex-end;
  gap: 10px;
}

/* View */
.creator-preview {
  display: flex;
  align-items: center;
  gap: 15px;
  padding-bottom: 22px;
  border-bottom: 1px solid #e2e8f0;
}

.preview-avatar {
  width: 70px;
  height: 70px;
  border-radius: 50%;
  overflow: hidden;
  background: #eff6ff;
  color: #2563eb;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 20px;
  font-weight: 700;
  flex-shrink: 0;
}

.preview-avatar img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.creator-preview h2 {
  margin: 0 0 5px;
  font-size: 20px;
}

.creator-preview p {
  margin: 0;
  color: #64748b;
  font-size: 13px;
}

.creator-preview p span {
  margin: 0 6px;
}

.detail-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 15px;
  margin-top: 22px;
}

.detail-item {
  padding: 14px;
  background: #f8fafc;
  border-radius: 9px;
}

.detail-item label {
  display: block;
  color: #64748b;
  font-size: 11px;
  margin-bottom: 5px;
}

.detail-item strong {
  color: #0f172a;
  font-size: 13px;
  line-height: 1.5;
}

.content-section {
  margin-top: 22px;
}

.content-section > label {
  display: block;
  margin-bottom: 8px;
  color: #334155;
  font-size: 13px;
  font-weight: 600;
}

.content-box {
  border: 1px solid #e2e8f0;
  border-radius: 9px;
  padding: 14px;
  color: #64748b;
  font-size: 13px;
  line-height: 1.7;
  white-space: pre-wrap;
  word-break: break-word;
}

/* Delete */
.delete-modal {
  width: 420px;
  max-width: 100%;
  background: #fff;
  border-radius: 14px;
  padding: 30px;
  text-align: center;
}

.delete-icon {
  width: 54px;
  height: 54px;
  margin: 0 auto 15px;
  border-radius: 50%;
  background: #fef2f2;
  color: #dc2626;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 25px;
}

.delete-modal h3 {
  margin: 0 0 8px;
  font-size: 18px;
}

.delete-modal p {
  margin: 0;
  color: #64748b;
  font-size: 14px;
  line-height: 1.6;
}

.delete-actions {
  display: flex;
  justify-content: center;
  gap: 10px;
  margin-top: 24px;
}

/* Responsive */
@media (max-width: 1000px) {
  .filter-card {
    flex-wrap: wrap;
  }

  .search-box {
    flex-basis: 100%;
  }

  .filter-card select {
    flex: 1;
  }
}

@media (max-width: 650px) {
  .creators-page {
    padding: 16px;
  }

  .page-header {
    align-items: flex-start;
  }

  .page-header h1 {
    font-size: 21px;
  }

  .page-header .primary-btn {
    padding: 9px 12px;
  }

  .page-header .primary-btn i {
    display: none;
  }

  .filter-card {
    display: block;
  }

  .search-box {
    margin-bottom: 10px;
  }

  .filter-card select {
    width: 100%;
    margin-bottom: 10px;
  }

  .reset-btn {
    width: 100%;
  }

  .form-grid {
    grid-template-columns: 1fr;
  }

  .form-group.full {
    grid-column: auto;
  }

  .detail-grid {
    grid-template-columns: 1fr;
  }

  .pagination {
    flex-direction: column;
    gap: 12px;
    align-items: flex-start;
  }
}
</style>